/**
 * Oynatma planı: karar → (gerekirse) transcode işi → durum izleme.
 *
 * Bu mantık `WatchScreen`'den ayrıdır çünkü **asenkron ve durumlu** tek
 * parçadır; ayrılmadan önce bileşen gereksiz yere karmaşıklaşıyordu.
 */
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Item, JobStatus, PlaybackDecision, TranscodeJob } from './api.ts';
import { api } from './api.ts';
import { detectClientCapabilities } from './clientCapabilities.ts';
import { clientProfileId } from './deviceProfile.ts';
import type { PlaybackPrefs } from './playbackPrefs.ts';
import { resumePositionMs } from './resume.ts';

export interface PlaybackPlan {
  decision: PlaybackDecision | null;
  job: TranscodeJob | null;
  status: JobStatus | null;
  /**
   * Bu planın başlayacağı **kaynak** konumu (ms).
   *
   * İlk açılışta kayıtlı ilerleme; kalite/yöntem değişiminde ise o anki canlı
   * oynatma konumu. Oynatıcı ve izleme oturumu bu değeri kullanır.
   */
  startPositionMs: number | null;
  /** Karar verilemedi ya da dönüştürme başarısız oldu. */
  failure: string | null;
}

/**
 * Web istemcisi bu profili taşır: Chromium tabanlı tarayıcı.
 *
 * **Not:** Profil artık sabit değil — telefon/tablet `web-mobile` profilini
 * kullanır (bkz. `deviceProfile.ts`). Bu sabit, `WatchScreen`'daki ikinci
 * kopyayla birlikte iki yerde tanımlıydı; tek kaynağa taşındı.
 */
export const PROFILE_ID = clientProfileId();

/** Durum izleme aralığı. Sunucu her cevapta kendi aralığını da bildirir. */
const POLL_INTERVAL_MS = 1000;

/** Yeni iş için başlangıç durumu (ilerleme henüz bilinmiyor). */
function toStatus(job: TranscodeJob): JobStatus {
  return {
    jobId: job.jobId,
    state: job.state,
    profile: job.profile,
    progressRatio: null,
    error: null,
  };
}

const EMPTY_PLAN: PlaybackPlan = {
  decision: null,
  job: null,
  status: null,
  startPositionMs: null,
  failure: null,
};

export function usePlaybackPlan(
  item: Item,
  forceTranscode = false,
  prefs: PlaybackPrefs = { quality: 'auto', mode: 'auto' },
  /** Seçilen ses akışı (`0:a:N`); transcode başlatılırken kullanılır. */
  audioStreamIndex = 0,
  /** Kullanıcı "Yeniden dene" dediğinde kararı yeniden çalıştırır. */
  retryKey = 0,
  /**
   * O anki **canlı** oynatma konumu (ms) — öğe kimliğiyle birlikte.
   *
   * Kalite düşürmesinde plan yeniden çalışır; kayıtlı ilerleme yerine bu değer
   * kullanılmalıdır, yoksa film en son kaydedilen noktaya (ya da başa) atlar.
   * Öğe değişiminde eski öğenin konumu sızmasın diye `itemId` verilir.
   *
   * Kanca bu işlevi **ref** üzerinden okur: kimliği her render'da değişse de
   * planın yeniden çalışmasına neden olmamalı.
   */
  getLivePositionMs?: (itemId: string) => number,
  /**
   * Başlangıç konumu. `WatchScreen` "Baştan başlat" seçildiğinde **0** geçer;
   * verilmezse kayıtlı ilerleme kullanılır.
   *
   * Bu parametre olmadan `usePlaybackPlan` her zaman `item.progress`'i okuyor,
   * dolayısıyla "Baştan başlat" yalnız arayüzde kalıyor ve film yine kaldığı
   * yerden başlıyordu (gerçek sunucuda gözlendi).
   */
  initialResumeMs?: number,
): PlaybackPlan {
  const [plan, setPlan] = useState<PlaybackPlan>(EMPTY_PLAN);

  const resumeMs = initialResumeMs ?? resumePositionMs(item);
  const { quality, mode } = prefs;
  usePlaybackDecision(
    item.id,
    resumeMs,
    forceTranscode,
    quality,
    mode,
    audioStreamIndex,
    setPlan,
    retryKey,
    getLivePositionMs,
  );

  const job = plan.job;
  const jobId = job === null ? null : job.jobId;
  const active = job !== null && (job.state === 'queued' || job.state === 'running');

  useEffect(() => {
    if (jobId === null || !active) return;

    const timer = window.setInterval(() => {
      void api
        .jobStatus(jobId)
        .then((status) => setPlan((previous) => ({ ...previous, status })))
        .catch(() => undefined);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [jobId, active]);

  return plan;
}

/**
 * Karar ister; transcode gerekiyorsa işi başlatır.
 *
 * `setPlan` üzerinden ilerler çünkü aynı state hem kararı hem işi taşır.
 * `itemId` ya da kaldığı konum değişirse yeniden karar verilir.
 */
function usePlaybackDecision(
  itemId: string,
  resumeMs: number,
  forceTranscode: boolean,
  quality: PlaybackPrefs['quality'],
  mode: PlaybackPrefs['mode'],
  audioStreamIndex: number,
  setPlan: Dispatch<SetStateAction<PlaybackPlan>>,
  retryKey: number,
  getLivePositionMs?: ((itemId: string) => number) | undefined,
): void {
  /**
   * Canlı konum okuyucusu **ref**'te tutulur.
   *
   * İşlevin kimliği her render'da değişebilir (video öğesi bağlanınca vb.);
   * bağımlılığa girseydi plan her render'da yeniden çalışır, her seferinde
   * yeni bir transcode işi açardı.
   */
  const livePositionRef = useRef(getLivePositionMs);
  livePositionRef.current = getLivePositionMs;

  useEffect(() => {
    // Yalnızca kullanıcı "Yeniden dene" dediğinde değişir; planı sıfırlar.
    void retryKey;
    let cancelled = false;
    setPlan(EMPTY_PLAN);

    /*
     * **Başlangıç konumu: kayıtlı ilerleme değil, o anki konum.**
     *
     * İlk açılışta video henüz yoktur → 0 döner ve kayıtlı ilerleme kullanılır.
     * Kalite düşürmesinde video oynuyordur → canlı konum kullanılır; böylece
     * yeni iş filmi baştan kodlamaz.
     */
    const liveMs = Math.max(0, Math.floor(livePositionRef.current?.(itemId) ?? 0));
    const startPositionMs = liveMs > 0 ? liveMs : resumeMs;

    void runPlan(
      itemId,
      startPositionMs,
      forceTranscode,
      { quality, mode },
      audioStreamIndex,
      (next) => {
        if (!cancelled) setPlan(next);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [itemId, resumeMs, forceTranscode, quality, mode, audioStreamIndex, retryKey, setPlan]);
}

/**
 * Kararı ister, gerekiyorsa transcode işini başlatır ve her adımda durumu bildirir.
 *
 * Üç aşamalı ilerleme bilinçlidir: arayüz kararı hemen gösterebilmeli
 * (gerekçe paneli), işi de hemen başlatabilmelidir (bekleme listesi ısınmasın).
 */
/**
 * "Doğrudan oynatılamıyor" mesajı — **çıkış yolunu da söyler**.
 *
 * Sunucunun gerekçesi teknik olabilir ("profilde mkv konteyneri için video
 * profili yok"). Kullanıcıya sebebi anlaşılır dille ve **ne yapabileceğini**
 * söyleyerek bildirir: dönüştürerek oynayabilirsiniz.
 */
export function unplayableMessage(decision: PlaybackDecision): string {
  const hints: Record<string, string> = {
    no_direct_play_profile: 'dosya biçimi tarayıcıda doğrudan açılamıyor (ör. MKV konteyneri)',
    no_direct_stream_profile: 'dosya biçimi tarayıcıda doğrudan açılamıyor (ör. MKV konteyneri)',
    video_codec_not_allowed: 'bu cihaz video kodunu çözemiyor',
    audio_codec_not_allowed: 'bu cihaz ses kodunu çözemiyor',
    dynamic_range_unsupported: 'bu cihaz HDR/Dolby Vision içeriğini açamıyor',
    bitrate_over_ceiling: 'bağlantı bant genişliği akışa yetmiyor',
  };

  const reason = decision.reasons.find((r) => hints[r.code] !== undefined);
  const cause = reason ? hints[reason.code] : null;

  return cause === null
    ? 'Bu içerik seçtiğiniz yöntemle oynatılamıyor. Sunucu üzerinden oynayabilirsiniz.'
    : `Bu içerik doğrudan oynatılamıyor: ${cause}. Sunucu üzerinden oynayabilirsiniz (dosya kopyalanabilir ya da dönüştürülebilir).`;
}

async function runPlan(
  itemId: string,
  startPositionMs: number,
  forceTranscode: boolean,
  prefs: PlaybackPrefs,
  audioStreamIndex: number,
  publish: (plan: PlaybackPlan) => void,
): Promise<void> {
  let decision: PlaybackDecision;
  const capabilities = detectClientCapabilities();
  try {
    decision = await api.decide({
      itemId,
      profileId: PROFILE_ID,
      startPositionMs,
      // Konum **sunucu tarafında** bağlantıdan türetilir; buradaki değer yalnız
      // bilgilendirme. Sunucu kendi türettiğini kullanır (bkz.
      // apps/server/src/playback/location.ts).
      location: 'lan',
      allowDirectPlay: !forceTranscode,
      allowDirectStream: !forceTranscode,
      // Kullanıcı tercihleri (ADR-011). `forceTranscode` (uyumluluk modu)
      // her zaman üstün gelir: o yolda doğrudan oynatma kapalıdır.
      quality: forceTranscode ? 'auto' : prefs.quality,
      mode: forceTranscode ? 'auto' : prefs.mode,
      clientCapabilities: forceTranscode
        ? { ...capabilities, hevc: false, hdr: false }
        : capabilities,
    });
  } catch (caught) {
    publish({
      ...EMPTY_PLAN,
      startPositionMs,
      failure: caught instanceof Error ? caught.message : 'Oynatma başlatılamadı.',
    });
    return;
  }

  publish({ ...EMPTY_PLAN, decision, startPositionMs });

  /*
   * Kullanıcı "doğrudan" seçti ama dosya doğrudan oynatılamıyor; sunucu
   * dönüştürmeye **geçmez** (kullanıcının seçimi), sonuç gerekçeyle
   * bildirilir.
   *
   * Mesaj **çıkış yolunu da söyler**: tek tıkla "dönüştürerek oynat"ya
   * geçilebilir. Aksi hâlde kullanıcı her denemede aynı duvara çarpar.
   */
  if (decision.mode === 'unplayable') {
    publish({
      ...EMPTY_PLAN,
      decision,
      failure: unplayableMessage(decision),
    });
    return;
  }

  if (decision.mode !== 'transcode' || !decision.transcode) return;

  // Karar hedef profili verdi; istemci kendi seçmez.
  try {
    /*
     * **Bant tavanı dönüştürmeye de taşınır.**
     *
     * Karar ucu tavanı hesapladı (bağlantı bütçesi ∩ profil bütçesi). Tavan
     * yalnız doğrudan oynatmayı engellemekle kalmamalı: dönüştürme çıktısı da
     * tavanın altında olmalı, yoksa telefon tavanı aşan bir akış çeker.
     */
    const job = await api.startTranscode(
      itemId,
      decision.transcode.profile,
      decision.maxBitrate ?? null,
      audioStreamIndex,
      startPositionMs,
    );
    publish({ decision, job, status: toStatus(job), startPositionMs, failure: null });
  } catch (caught) {
    publish({
      ...EMPTY_PLAN,
      startPositionMs,
      failure: caught instanceof Error ? caught.message : 'Dönüştürme başlatılamadı.',
    });
  }
}
